'use client';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Search, ChevronDown, ChevronUp, MessageSquare, Ticket, Phone, Mail, CheckCircle, Loader2, AlertCircle, BookOpen, CreditCard, Image, MapPin, Package, HelpCircle, AlertTriangle, Wrench, X, Send, Bot, User, Minimize2, Maximize2,  } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ── FAQ Data ──────────────────────────────────────────────────────────────────

interface FaqItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

const FAQ_CATEGORIES = [
  { id: 'all', label: 'All Topics', icon: HelpCircle },
  { id: 'getting-started', label: 'Getting Started', icon: BookOpen },
  { id: 'billing', label: 'Billing & Payments', icon: CreditCard },
  { id: 'artwork', label: 'Artwork & Design', icon: Image },
  { id: 'regions', label: 'Regions & Coverage', icon: MapPin },
  { id: 'orders', label: 'Orders & Delivery', icon: Package },
];

const FAQ_ITEMS: FaqItem[] = [
  {
    id: 'faq-1',
    category: 'getting-started',
    question: 'What is ZipAdBook and how does it work?',
    answer:
      'ZipAdBook is a hyperlocal coupon book distributed to 1,000+ businesses per region every 5 weeks across Florida and Puerto Rico. Advertisers book placements online, submit their artwork, and we handle printing and distribution. Your ad reaches real local businesses in your chosen city.',
  },
  {
    id: 'faq-2',
    category: 'getting-started',
    question: 'How do I book my first ad?',
    answer:
      'Click "Book an Ad" in the top navigation. You\'ll walk through a step-by-step wizard: choose your placement type, select your target regions, pick your issue dates, upload or request artwork, and complete payment. The whole process takes under 10 minutes.',
  },
  {
    id: 'faq-3',
    category: 'getting-started',
    question: 'What placement types are available?',
    answer:
      'We currently offer Full Page placements (8.5 x 5.5). Half Page placements are coming soon.',
  },
  {
    id: 'faq-4',
    category: 'billing',
    question: 'What payment methods do you accept?',
    answer:
      'We accept all major credit and debit cards (Visa, Mastercard, American Express, Discover) processed securely through Stripe. We also offer 3-issue bundle pricing for advertisers who want to commit to multiple runs at a discounted rate.',
  },
  {
    id: 'faq-5',
    category: 'billing',
    question: 'Can I get a refund after booking?',
    answer:
      'No refunds. A credit will be applied that can be used for a future booking. Once your artwork has been approved and sent to print, we are unable to issue refunds. Please contact our support team as soon as possible if you need to cancel.',
  },
  {
    id: 'faq-6',
    category: 'billing',
    question: 'How do I update my payment method?',
    answer:
      'Go to My Profile → Billing tab and click "Update Payment." This opens the Stripe billing portal where you can securely update your saved card. Changes apply to future charges immediately.',
  },
  {
    id: 'faq-7',
    category: 'artwork',
    question: 'What are the artwork specifications?',
    answer:
      'Artwork must be submitted as a high-resolution PDF or print-ready file at 300 DPI minimum. Dimensions vary by placement — you\'ll receive exact specs after booking. Files should include 0.125" bleed on all sides and use CMYK color mode for accurate print reproduction.',
  },
  {
    id: 'faq-8',
    category: 'artwork',
    question: 'What if I don\'t have artwork ready?',
    answer:
      'Our design team can create your ad for you. Select "Request Design Assistance" during the order wizard and our team will reach out within 1 business day. Design fees vary by complexity — contact us for a quote.',
  },
  {
    id: 'faq-9',
    category: 'artwork',
    question: 'When is the artwork submission deadline?',
    answer:
      'Artwork must be submitted at least 10 business days before your issue\'s print date. You\'ll receive automated reminder emails at 10, 7, and 3 days before the deadline. You can also check your deadline in My Orders or the Order Dashboard.',
  },
  {
    id: 'faq-10',
    category: 'regions',
    question: 'Which regions do you cover?',
    answer:
      'We currently distribute across multiple regions in Florida and Puerto Rico. Each region covers approximately 1,000 businesses within a defined zip code cluster. Visit our Regions & Pricing page to see a full map and availability by region.',
  },
  {
    id: 'faq-11',
    category: 'regions',
    question: 'Can I advertise in multiple regions at once?',
    answer:
      'Yes! During the order wizard you can select multiple regions for a single placement. Each additional region is priced individually. Bundle discounts apply when booking 3+ issues across the same regions.',
  },
  {
    id: 'faq-12',
    category: 'orders',
    question: 'How do I track my order status?',
    answer:
      'Log in and visit My Orders or the Order Dashboard to see real-time status for all your placements. Statuses include: Pending, Artwork Submitted, Artwork Approved, In Print, Campaign Live, and Completed. You\'ll also receive email notifications at each key milestone.',
  },
  {
    id: 'faq-13',
    category: 'orders',
    question: 'When will my ad be distributed?',
    answer:
      'Distribution happens on a rolling 5-week cycle. Your confirmed issue date is shown in your order details. Books are typically distributed over a 2–3 day window at the start of each cycle. You\'ll receive a "Campaign Live" email notification when distribution begins.',
  },
];

// ── Troubleshooting Guides ────────────────────────────────────────────────────

interface TroubleshootingGuide {
  id: string;
  title: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  steps: { step: string; detail: string }[];
}

const TROUBLESHOOTING_GUIDES: TroubleshootingGuide[] = [
  {
    id: 'payment-failed',
    title: 'Payment Failed or Declined',
    icon: <CreditCard size={16} />,
    color: 'text-red-400',
    bgColor: 'bg-red-500/15 border-red-500/25',
    steps: [
      { step: 'Check your card details', detail: 'Verify the card number, expiry date, and CVV are entered correctly. Typos are the most common cause.' },
      { step: 'Confirm billing address', detail: 'Your billing address must match what your bank has on file. Even a zip code mismatch can trigger a decline.' },
      { step: 'Contact your bank', detail: 'Some banks block online transactions by default. Call the number on the back of your card to authorize the charge.' },
      { step: 'Try a different card', detail: 'If the issue persists, try a different credit or debit card. Prepaid cards are not accepted.' },
      { step: 'Still stuck?', detail: 'Contact our support team with your order number and we\'ll help resolve the payment issue within 1 business day.' },
    ],
  },
  {
    id: 'artwork-rejected',
    title: 'Artwork Rejected or Needs Revision',
    icon: <Image size={16} />,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/15 border-amber-500/25',
    steps: [
      { step: 'Review the rejection reason', detail: 'Check your email for the rejection notice — it includes specific feedback from our design team about what needs to change.' },
      { step: 'Check file specifications', detail: 'Ensure your file is 300 DPI minimum, includes 0.125" bleed, uses CMYK color mode, and is saved as a print-ready PDF.' },
      { step: 'Re-upload corrected artwork', detail: 'Log in → My Orders → select your order → Upload Artwork. You can re-submit as many times as needed before the deadline.' },
      { step: 'Request design help', detail: 'If you\'re unsure how to fix the issue, reply to the rejection email and our design team can assist or create the ad for you.' },
    ],
  },
  {
    id: 'order-not-showing',
    title: 'Order Not Showing in Dashboard',
    icon: <Package size={16} />,
    color: 'text-sky-400',
    bgColor: 'bg-sky-500/15 border-sky-500/25',
    steps: [
      { step: 'Check your email for confirmation', detail: 'A confirmation email is sent immediately after a successful order. If you didn\'t receive one, the order may not have completed.' },
      { step: 'Verify you\'re logged in to the right account', detail: 'Make sure you\'re signed in with the same email address used during checkout.' },
      { step: 'Refresh the page', detail: 'Sometimes a hard refresh (Ctrl+Shift+R / Cmd+Shift+R) is needed to load the latest order data.' },
      { step: 'Check payment status', detail: 'If payment was pending or failed, the order may be in a draft state. Check your Stripe receipt email for payment confirmation.' },
      { step: 'Contact support', detail: 'If your order still doesn\'t appear after 30 minutes, contact us with your payment confirmation number and we\'ll locate it.' },
    ],
  },
  {
    id: 'missed-deadline',
    title: 'Missed Artwork Deadline',
    icon: <AlertTriangle size={16} />,
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/15 border-orange-500/25',
    steps: [
      { step: 'Contact us immediately', detail: 'Call +1 (321) 265-3177 or email support@zipadbook.com right away. We may be able to accommodate late submissions depending on print schedule.' },
      { step: 'Check if you\'re within the grace window', detail: 'We sometimes have a 24–48 hour grace window after the deadline. The sooner you reach out, the better.' },
      { step: 'Request a rollover to the next issue', detail: 'If the deadline has passed, we can roll your booking to the next available issue at no additional charge.' },
      { step: 'Use our design service', detail: 'To avoid future deadline issues, consider our design service — we\'ll create your ad and submit it on your behalf.' },
    ],
  },
];

// ── Form Types ────────────────────────────────────────────────────────────────

interface FormState {
  name: string;
  email: string;
  subject: string;
  message: string;
}

type FormStatus = 'idle' | 'loading' | 'success' | 'error';

const INITIAL_FORM: FormState = { name: '', email: '', subject: '', message: '' };

// ── Live Chat Widget ──────────────────────────────────────────────────────────

interface ChatMessage {
  id: string;
  role: 'bot' | 'user';
  text: string;
  time: string;
}

const BOT_RESPONSES: Record<string, string> = {
  default: "Thanks for reaching out! Our support team typically responds within 1–2 business hours during business hours (Mon–Fri, 9am–6pm ET). For urgent issues, please call us at +1 (321) 265-3177.",
  payment: "For payment issues, please check that your card details and billing address are correct. If the problem persists, contact your bank or try a different card. You can also email support@zipadbook.com with your order number.",
  artwork: "For artwork questions, make sure your file is 300 DPI, includes 0.125\" bleed, and is saved as a print-ready PDF in CMYK. Check your order dashboard for your specific deadline.",
  order: "You can track your order status in My Dashboard → Order History, or visit the Order Status page directly. You'll also receive email updates at each milestone.",
  refund: "Our policy is no refunds — a credit will be applied for future use. If you need to cancel before artwork approval, please contact us immediately at support@zipadbook.com.",
  cancel: "Cancellations are only possible before artwork is approved. Please contact us at support@zipadbook.com or call +1 (321) 265-3177 as soon as possible.",
};

function getAutoResponse(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('payment') || lower.includes('card') || lower.includes('charge') || lower.includes('billing')) return BOT_RESPONSES.payment;
  if (lower.includes('artwork') || lower.includes('design') || lower.includes('file') || lower.includes('deadline')) return BOT_RESPONSES.artwork;
  if (lower.includes('order') || lower.includes('track') || lower.includes('status') || lower.includes('dashboard')) return BOT_RESPONSES.order;
  if (lower.includes('refund') || lower.includes('money back')) return BOT_RESPONSES.refund;
  if (lower.includes('cancel')) return BOT_RESPONSES.cancel;
  return BOT_RESPONSES.default;
}

function LiveChatWidget() {
  const { user } = useAuth();
  const supabaseRef = useRef(createClient());
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestInfoSubmitted, setGuestInfoSubmitted] = useState(false);
  const [rootMessageId, setRootMessageId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'bot',
      text: "Hi! 👋 I'm the ZipAdBook support assistant. How can I help you today? You can ask about payments, artwork, orders, or anything else.",
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    },
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen, isMinimized]);

  // Real-time: listen for admin/manager replies on the root thread
  useEffect(() => {
    if (!rootMessageId) return;
    const supabase = supabaseRef.current;
    const channel = supabase
      .channel(`chat-widget-replies-${rootMessageId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'support_messages', filter: `parent_id=eq.${rootMessageId}` },
        (payload: any) => {
          const row = payload.new;
          if (row.sender_role !== 'customer') {
            const now = new Date(row.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
            setMessages((prev) => [
              ...prev,
              { id: row.id, role: 'bot', text: row.message, time: now },
            ]);
          }
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [rootMessageId]);

  const sendMessage = async () => {
    const text = inputValue.trim();
    if (!text) return;

    const now = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    const userMsg: ChatMessage = { id: `user-${Date.now()}`, role: 'user', text, time: now };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);

    // Save to Supabase support_messages
    try {
      const supabase = supabaseRef.current;
      const isFirstMessage = rootMessageId === null;

      if (isFirstMessage) {
        // Insert root message
        const insertPayload: Record<string, any> = {
          subject: text.length > 80 ? text.slice(0, 80) + '…' : text,
          category: 'general',
          message: text,
          sender_role: 'customer',
          is_read: false,
          source: 'customer',
        };
        if (user?.id) {
          insertPayload.user_id = user.id;
        } else {
          insertPayload.customer_name = guestName || 'Website Visitor';
          insertPayload.phone_number = guestEmail || null;
        }

        const { data, error } = await supabase
          .from('support_messages')
          .insert(insertPayload)
          .select('id')
          .single();

        if (!error && data?.id) {
          setRootMessageId(data.id);
        }
      } else {
        // Insert reply in thread
        const replyPayload: Record<string, any> = {
          subject: 'Chat Widget Reply',
          category: 'general',
          message: text,
          sender_role: 'customer',
          is_read: false,
          parent_id: rootMessageId,
          source: 'customer',
        };
        if (user?.id) {
          replyPayload.user_id = user.id;
        } else {
          replyPayload.customer_name = guestName || 'Website Visitor';
        }
        await supabase.from('support_messages').insert(replyPayload);
      }
    } catch {
      // silently continue — still show bot response
    }

    setTimeout(() => {
      const botText = getAutoResponse(text);
      const botMsg: ChatMessage = {
        id: `bot-${Date.now()}`,
        role: 'bot',
        text: botText,
        time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages((prev) => [...prev, botMsg]);
      setIsTyping(false);
    }, 1200);
  };

  // For unauthenticated users, show a brief name/email capture before first message
  const needsGuestInfo = !user && !guestInfoSubmitted;

  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-brand-600 hover:bg-brand-500 shadow-lg shadow-brand-600/30 flex items-center justify-center transition-all duration-200 hover:scale-105 touch-manipulation"
          aria-label="Open live chat"
        >
          <MessageSquare size={22} className="text-white" />
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-green-400 border-2 border-navy-900" />
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div className={`fixed bottom-6 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[360px] rounded-2xl overflow-hidden shadow-2xl shadow-black/40 border border-white/12 flex flex-col transition-all duration-200 ${isMinimized ? 'h-14' : 'h-[480px] sm:h-[520px]'}`}
          style={{ background: 'rgb(10 15 30)' }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-brand-600/20 border-b border-white/10 shrink-0">
            <div className="w-8 h-8 rounded-full bg-brand-600/30 border border-brand-500/40 flex items-center justify-center shrink-0">
              <Bot size={15} className="text-brand-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white leading-none">ZipAdBook Support</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                <span className="text-[11px] text-slate-400">Online · Typically replies in minutes</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsMinimized((v) => !v)}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors touch-manipulation"
                aria-label={isMinimized ? 'Expand chat' : 'Minimize chat'}
              >
                {isMinimized ? <Maximize2 size={13} /> : <Minimize2 size={13} />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors touch-manipulation"
                aria-label="Close chat"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Guest info capture (unauthenticated only, before first message) */}
              {needsGuestInfo ? (
                <div className="flex-1 flex flex-col justify-center px-5 py-6 gap-4">
                  <p className="text-sm text-slate-300 text-center leading-relaxed">
                    Before we start, please share your name and email so our team can follow up.
                  </p>
                  <input
                    type="text"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="Your name"
                    className="w-full px-3 py-2.5 rounded-xl bg-white/6 border border-white/12 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500/50 transition-all"
                  />
                  <input
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="Your email (optional)"
                    className="w-full px-3 py-2.5 rounded-xl bg-white/6 border border-white/12 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500/50 transition-all"
                  />
                  <button
                    onClick={() => { if (guestName.trim()) setGuestInfoSubmitted(true); }}
                    disabled={!guestName.trim()}
                    className="w-full py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-sm font-semibold text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Start Chat
                  </button>
                  <button
                    onClick={() => setGuestInfoSubmitted(true)}
                    className="text-xs text-slate-500 hover:text-slate-400 transition-colors text-center"
                  >
                    Skip — chat anonymously
                  </button>
                </div>
              ) : (
                <>
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-thin">
                    {messages.map((msg) => (
                      <div key={msg.id} className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                          msg.role === 'bot' ? 'bg-brand-600/25 border border-brand-500/30' : 'bg-white/10 border border-white/15'
                        }`}>
                          {msg.role === 'bot' ? <Bot size={13} className="text-brand-300" /> : <User size={13} className="text-slate-300" />}
                        </div>
                        <div className={`max-w-[78%] ${msg.role === 'user' ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                          <div className={`px-3 py-2.5 rounded-2xl text-xs leading-relaxed ${
                            msg.role === 'bot' ?'bg-white/8 border border-white/10 text-slate-300 rounded-tl-sm' :'bg-brand-600/30 border border-brand-500/30 text-white rounded-tr-sm'
                          }`}>
                            {msg.text}
                          </div>
                          <span className="text-[10px] text-slate-600 px-1">{msg.time}</span>
                        </div>
                      </div>
                    ))}
                    {isTyping && (
                      <div className="flex gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-brand-600/25 border border-brand-500/30 flex items-center justify-center shrink-0">
                          <Bot size={13} className="text-brand-300" />
                        </div>
                        <div className="px-3 py-2.5 rounded-2xl rounded-tl-sm bg-white/8 border border-white/10 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Quick replies */}
                  <div className="px-4 pb-2 flex gap-1.5 overflow-x-auto scrollbar-thin shrink-0">
                    {['Payment issue', 'Artwork help', 'Track order', 'Cancel order'].map((q) => (
                      <button
                        key={q}
                        onClick={() => { setInputValue(q); }}
                        className="px-2.5 py-1.5 rounded-full bg-white/6 border border-white/10 text-[11px] text-slate-400 hover:text-white hover:bg-white/10 transition-colors whitespace-nowrap shrink-0 touch-manipulation"
                      >
                        {q}
                      </button>
                    ))}
                  </div>

                  {/* Input */}
                  <div className="px-3 pb-3 shrink-0">
                    <div className="flex items-center gap-2 bg-white/6 border border-white/12 rounded-xl px-3 py-2">
                      <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                        placeholder="Type your message…"
                        className="flex-1 bg-transparent text-sm text-white placeholder-slate-500 focus:outline-none min-w-0"
                      />
                      <button
                        onClick={sendMessage}
                        disabled={!inputValue.trim()}
                        className="w-8 h-8 rounded-lg bg-brand-600 hover:bg-brand-500 flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0 touch-manipulation"
                        aria-label="Send message"
                      >
                        <Send size={13} className="text-white" />
                      </button>
                    </div>
                    <p className="text-[10px] text-slate-600 text-center mt-1.5">
                      For urgent help call{' '}
                      <a href="tel:+13212653177" className="text-brand-400 hover:text-brand-300">+1 (321) 265-3177</a>
                    </p>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

// ── Troubleshooting Section ───────────────────────────────────────────────────

function TroubleshootingSection() {
  const [openGuideId, setOpenGuideId] = useState<string | null>(null);

  return (
    <div className="mt-16 sm:mt-20">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 rounded-xl bg-orange-500/15 border border-orange-500/25 flex items-center justify-center shrink-0">
          <Wrench size={16} className="text-orange-400" />
        </div>
        <div>
          <h2 className="font-display text-xl font-700 text-white">Order Troubleshooting Guides</h2>
          <p className="text-xs text-slate-400 mt-0.5">Step-by-step solutions for common issues</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {TROUBLESHOOTING_GUIDES.map((guide) => {
          const isOpen = openGuideId === guide.id;
          return (
            <div key={guide.id} className="glass-card rounded-2xl overflow-hidden border border-white/8">
              <button
                onClick={() => setOpenGuideId(isOpen ? null : guide.id)}
                className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/3 transition-colors touch-manipulation"
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border ${guide.bgColor}`}>
                  <span className={guide.color}>{guide.icon}</span>
                </div>
                <span className="flex-1 text-sm font-semibold text-white leading-snug">{guide.title}</span>
                <ChevronDown
                  size={16}
                  className={`text-slate-500 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isOpen && (
                <div className="px-5 pb-5 animate-fade-in">
                  <div className="h-px bg-white/6 mb-4" />
                  <ol className="space-y-4">
                    {guide.steps.map((item, idx) => (
                      <li key={idx} className="flex gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 border ${guide.bgColor} ${guide.color}`}>
                          {idx + 1}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white mb-0.5">{item.step}</p>
                          <p className="text-xs text-slate-400 leading-relaxed">{item.detail}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                  <div className="mt-4 pt-4 border-t border-white/6">
                    <p className="text-xs text-slate-500">
                      Still need help?{' '}
                      <a href="mailto:support@zipadbook.com" className="text-brand-400 hover:text-brand-300 transition-colors">
                        Email our support team
                      </a>{' '}
                      or call{' '}
                      <a href="tel:+13212653177" className="text-brand-400 hover:text-brand-300 transition-colors">
                        +1 (321) 265-3177
                      </a>
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function HelpContent() {
  // FAQ state
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);

  // Forms state
  const [activeForm, setActiveForm] = useState<'contact' | 'ticket'>('contact');
  const [contactForm, setContactForm] = useState<FormState>(INITIAL_FORM);
  const [ticketForm, setTicketForm] = useState<FormState>(INITIAL_FORM);
  const [contactStatus, setContactStatus] = useState<FormStatus>('idle');
  const [ticketStatus, setTicketStatus] = useState<FormStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // Filtered FAQs
  const filteredFaqs = useMemo(() => {
    return FAQ_ITEMS.filter((item) => {
      const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        item.question.toLowerCase().includes(q) ||
        item.answer.toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, activeCategory]);

  const toggleFaq = (id: string) => setOpenFaqId((prev) => (prev === id ? null : id));

  const handleSubmit = async (type: 'contact' | 'ticket') => {
    const form = type === 'contact' ? contactForm : ticketForm;
    const setStatus = type === 'contact' ? setContactStatus : setTicketStatus;

    if (!form.name || !form.email || !form.subject || !form.message) {
      setErrorMsg('Please fill in all fields.');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('/api/help/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, type }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Something went wrong.');
      }

      setStatus('success');
      if (type === 'contact') setContactForm(INITIAL_FORM);
      else setTicketForm(INITIAL_FORM);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Failed to send. Please try again.');
    }
  };

  const currentForm = activeForm === 'contact' ? contactForm : ticketForm;
  const setCurrentForm = activeForm === 'contact' ? setContactForm : setTicketForm;
  const currentStatus = activeForm === 'contact' ? contactStatus : ticketStatus;

  return (
    <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 lg:py-20">

      {/* ── Hero ── */}
      <div className="text-center mb-10 sm:mb-14">
        <span className="chip mb-4">Support Center</span>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-800 text-white mb-4">
          How can we <span className="text-gradient">help you?</span>
        </h1>
        <p className="text-slate-400 text-base sm:text-lg max-w-xl mx-auto">
          Browse our FAQ, submit a support ticket, or reach us directly — we typically respond within 1–2 business days.
        </p>
      </div>

      {/* ── Quick Contact Strip ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-12 sm:mb-16 max-w-2xl mx-auto">
        <a
          href="tel:+13212653177"
          className="glass-card rounded-2xl p-4 sm:p-5 flex items-center gap-4 glass-card-hover group touch-manipulation"
        >
          <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-brand-600/20 border border-brand-500/30 flex items-center justify-center shrink-0 group-hover:bg-brand-600/30 transition-colors">
            <Phone size={18} className="text-brand-400" />
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-0.5">Call us directly</p>
            <p className="text-sm font-semibold text-white">+1 (321) 265-3177</p>
          </div>
          <span className="ml-auto w-2 h-2 rounded-full bg-green-400 animate-pulse shrink-0" />
        </a>
        <a
          href="mailto:support@zipadbook.com"
          className="glass-card rounded-2xl p-4 sm:p-5 flex items-center gap-4 glass-card-hover group touch-manipulation"
        >
          <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-brand-600/20 border border-brand-500/30 flex items-center justify-center shrink-0 group-hover:bg-brand-600/30 transition-colors">
            <Mail size={18} className="text-brand-400" />
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-0.5">Email support</p>
            <p className="text-sm font-semibold text-white">support@zipadbook.com</p>
          </div>
        </a>
      </div>

      {/* ── Main Grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 sm:gap-10">

        {/* ── Left: FAQ ── */}
        <div className="lg:col-span-3">
          <div className="flex items-center gap-3 mb-5 sm:mb-6">
            <div className="w-9 h-9 rounded-xl bg-brand-600/20 border border-brand-500/30 flex items-center justify-center shrink-0">
              <HelpCircle size={16} className="text-brand-400" />
            </div>
            <h2 className="font-display text-lg sm:text-xl font-700 text-white">Frequently Asked Questions</h2>
          </div>

          {/* Search */}
          <div className="relative mb-4 sm:mb-5">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <input
              type="text"
              placeholder="Search questions…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-navy-700/60 border border-white/10 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500/60 focus:bg-navy-700/80 transition-all min-h-[44px]"
            />
          </div>

          {/* Category Chips — horizontally scrollable on mobile */}
          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 pb-1 mb-5 sm:mb-6">
            <div className="flex gap-2 w-fit sm:flex-wrap">
              {FAQ_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`chip transition-all whitespace-nowrap shrink-0 touch-manipulation min-h-[36px] ${activeCategory === cat.id ? 'chip-selected' : 'hover:chip-selected'}`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* FAQ List */}
          <div className="space-y-2">
            {filteredFaqs.length === 0 ? (
              <div className="glass-card rounded-2xl p-10 text-center">
                <Search size={32} className="text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">No results for "{searchQuery}"</p>
                <button
                  onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}
                  className="mt-3 text-xs text-brand-400 hover:text-brand-300 transition-colors"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              filteredFaqs.map((item) => (
                <div
                  key={item.id}
                  className="glass-card rounded-xl overflow-hidden transition-all duration-200"
                >
                  <button
                    onClick={() => toggleFaq(item.id)}
                    className="w-full flex items-center justify-between gap-4 px-4 sm:px-5 py-4 text-left hover:bg-white/3 transition-colors touch-manipulation min-h-[52px]"
                  >
                    <span className="text-sm font-medium text-white leading-snug">{item.question}</span>
                    {openFaqId === item.id ? (
                      <ChevronUp size={16} className="text-brand-400 shrink-0" />
                    ) : (
                      <ChevronDown size={16} className="text-slate-500 shrink-0" />
                    )}
                  </button>
                  {openFaqId === item.id && (
                    <div className="px-4 sm:px-5 pb-5 animate-fade-in">
                      <div className="h-px bg-white/6 mb-4" />
                      <p className="text-sm text-slate-400 leading-relaxed">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {filteredFaqs.length > 0 && (
            <p className="text-xs text-slate-600 mt-4 text-center">
              Showing {filteredFaqs.length} of {FAQ_ITEMS.length} questions
            </p>
          )}
        </div>

        {/* ── Right: Forms ── */}
        <div className="lg:col-span-2">
          {/* Tab Toggle */}
          <div className="flex items-center gap-2 mb-5 sm:mb-6">
            <button
              onClick={() => { setActiveForm('contact'); setErrorMsg(''); }}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl text-sm font-medium transition-all min-h-[44px] touch-manipulation ${
                activeForm === 'contact' ?'bg-brand-600/20 border border-brand-500/40 text-brand-300' :'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <MessageSquare size={15} />
              Contact Us
            </button>
            <button
              onClick={() => { setActiveForm('ticket'); setErrorMsg(''); }}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl text-sm font-medium transition-all min-h-[44px] touch-manipulation ${
                activeForm === 'ticket' ?'bg-brand-600/20 border border-brand-500/40 text-brand-300' :'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Ticket size={15} />
              Support Ticket
            </button>
          </div>

          <div className="glass-card rounded-2xl p-5 sm:p-6">
            {currentStatus === 'success' ? (
              <div className="py-10 text-center animate-fade-in">
                <div className="w-14 h-14 rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={26} className="text-green-400" />
                </div>
                <h3 className="font-display text-lg font-700 text-white mb-2">Message Sent!</h3>
                <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                  {activeForm === 'ticket' ? "Your support ticket has been submitted. We'll respond within 1–2 business days." :"Thanks for reaching out! We'll get back to you within 1–2 business days."}
                </p>
                <button
                  onClick={() => {
                    if (activeForm === 'contact') setContactStatus('idle');
                    else setTicketStatus('idle');
                  }}
                  className="text-sm text-brand-400 hover:text-brand-300 transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl bg-brand-600/20 border border-brand-500/30 flex items-center justify-center shrink-0">
                    {activeForm === 'contact' ? (
                      <MessageSquare size={15} className="text-brand-400" />
                    ) : (
                      <Ticket size={15} className="text-brand-400" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-display text-base font-700 text-white">
                      {activeForm === 'contact' ? 'Send a Message' : 'Submit a Ticket'}
                    </h3>
                    <p className="text-xs text-slate-500">
                      {activeForm === 'contact' ?'General inquiries & questions' :'Technical issues & order problems'}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Name + Email — stacked on mobile, side-by-side on sm+ */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1.5 font-medium">Name</label>
                      <input
                        type="text"
                        placeholder="Your name"
                        value={currentForm.name}
                        onChange={(e) => setCurrentForm((f) => ({ ...f, name: e.target.value }))}
                        className="w-full px-3 py-2.5 bg-navy-700/60 border border-white/10 rounded-xl text-sm text-white placeholder-slate-600 focus:outline-none focus:border-brand-500/60 transition-all min-h-[44px]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1.5 font-medium">Email</label>
                      <input
                        type="email"
                        placeholder="you@example.com"
                        value={currentForm.email}
                        onChange={(e) => setCurrentForm((f) => ({ ...f, email: e.target.value }))}
                        className="w-full px-3 py-2.5 bg-navy-700/60 border border-white/10 rounded-xl text-sm text-white placeholder-slate-600 focus:outline-none focus:border-brand-500/60 transition-all min-h-[44px]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-slate-500 mb-1.5 font-medium">Subject</label>
                    <input
                      type="text"
                      placeholder={activeForm === 'ticket' ? 'Briefly describe your issue' : 'What can we help with?'}
                      value={currentForm.subject}
                      onChange={(e) => setCurrentForm((f) => ({ ...f, subject: e.target.value }))}
                      className="w-full px-3 py-2.5 bg-navy-700/60 border border-white/10 rounded-xl text-sm text-white placeholder-slate-600 focus:outline-none focus:border-brand-500/60 transition-all min-h-[44px]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-slate-500 mb-1.5 font-medium">Message</label>
                    <textarea
                      rows={5}
                      placeholder={
                        activeForm === 'ticket' ?'Describe the issue in detail. Include your order number if applicable.' :'Tell us how we can help…'
                      }
                      value={currentForm.message}
                      onChange={(e) => setCurrentForm((f) => ({ ...f, message: e.target.value }))}
                      className="w-full px-3 py-2.5 bg-navy-700/60 border border-white/10 rounded-xl text-sm text-white placeholder-slate-600 focus:outline-none focus:border-brand-500/60 transition-all resize-none"
                    />
                  </div>

                  {(currentStatus === 'error' || errorMsg) && (
                    <div className="flex items-start gap-2.5 p-3 bg-red-500/10 border border-red-500/25 rounded-xl animate-fade-in">
                      <AlertCircle size={15} className="text-red-400 shrink-0 mt-0.5" />
                      <p className="text-xs text-red-300">{errorMsg || 'Something went wrong. Please try again.'}</p>
                    </div>
                  )}

                  {/* SMS Consent */}
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 w-4 h-4 rounded border-white/20 bg-navy-700 accent-brand-500 shrink-0"
                    />
                    <span className="text-xs text-slate-400 leading-relaxed">
                      By checking this box, you agree to receive SMS messages from ZipAdBBook by Take A Zip LLC related to your Placements, Ads or Flyer distribution services inquiry and follow-ups. Message &amp; data rates may apply. Message frequency varies. Reply STOP to unsubscribe, HELP for help. Take A Zip LLC.
                    </span>
                  </label>

                  <button
                    onClick={() => handleSubmit(activeForm)}
                    disabled={currentStatus === 'loading'}
                    className="w-full py-3 text-sm font-semibold btn-primary flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none min-h-[48px]"
                  >
                    {currentStatus === 'loading' ? (
                      <>
                        <Loader2 size={15} className="animate-spin" />
                        Sending…
                      </>
                    ) : activeForm === 'ticket' ? (
                      <>
                        <Ticket size={15} />
                        Submit Ticket
                      </>
                    ) : (
                      <>
                        <MessageSquare size={15} />
                        Send Message
                      </>
                    )}
                  </button>

                  <p className="text-xs text-slate-600 text-center">
                    We respond within 1–2 business days. For urgent issues, call{' '}
                    <a href="tel:+13212653177" className="text-brand-400 hover:text-brand-300 transition-colors">
                      +1 (321) 265-3177
                    </a>
                    .
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Troubleshooting Guides ── */}
      <TroubleshootingSection />

      {/* ── Live Chat Widget ── */}
      <LiveChatWidget />
    </div>
  );
}
